import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface BusinessSettings {
  id: string;
  default_hourly_rate: number;
  default_platform: string;
  legal_name: string | null;
  cnpj: string | null;
  pix_key: string | null;
  max_installments: number;
  /** Cap (R$) on permuta agreed per period; 0 = no cap. */
  barter_limit_amount: number;
  barter_limit_period: "month" | "year";
  updated_at: string;
}

export type BusinessSettingsUpdate = Partial<
  Pick<
    BusinessSettings,
    | "default_hourly_rate"
    | "default_platform"
    | "legal_name"
    | "cnpj"
    | "pix_key"
    | "max_installments"
    | "barter_limit_amount"
    | "barter_limit_period"
  >
>;

export function useBusinessSettings() {
  return useQuery({
    queryKey: ["business-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_settings" as never)
        .select("*")
        .limit(1)
        .single();
      if (error) throw error;
      return data as unknown as BusinessSettings;
    },
  });
}

export function useUpdateBusinessSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: BusinessSettingsUpdate) => {
      const { data: current, error: fetchError } = await supabase
        .from("business_settings" as never)
        .select("id")
        .limit(1)
        .single();
      if (fetchError) throw fetchError;

      const { data: result, error } = await supabase
        .from("business_settings" as never)
        .update(data as never)
        .eq("id", (current as unknown as { id: string }).id)
        .select()
        .single();
      if (error) throw error;
      return result as unknown as BusinessSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["business-settings"] });
      toast.success("Configurações de cobrança atualizadas!");
    },
    onError: (error) => toast.error("Erro: " + error.message),
  });
}
